import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Midi } from '@tonejs/midi';
import * as Tone from 'tone';
import { Piano } from '@tonejs/piano';
import './App.css';

// 88 keys (full piano): display & play all; one-key input (key 1 only)
const NUM_LANES = 88;
const NUM_LANES_PER_PLAYER = 44; // two-player: left 0-43 = P1, right 44-87 = P2
const LANE_P2_START = 44;
const MIDI_LOW = 21; // A0
const NOTE_FREQUENCIES = Array.from({ length: NUM_LANES }, (_, i) =>
  440 * Math.pow(2, (MIDI_LOW + i - 69) / 12)
);
// Score: press any key when notes are in the hit window → +10 × number of notes
const BASE_POINTS = 10;
// All notes white
const LANE_COLORS = Array.from({ length: NUM_LANES }, () => '#f5f5f5');

// MIDI file list (add any new file from public/midi/ here to show in the dropdown)
const MIDI_FILES = [
  'Canon_in_D.mid',
  'Clair_de_Lune__Debussy.mid',
  'Ed_Sheeran___Perfect_Piano_Arrangement.mid',
  'Hes_a_Pirate.mid',
  'MerryGoRound_of_Life_Howls_Moving_Castle_Piano_Tutorial.mid',
  'My_Heart_Will_Go_On.mid',
  'River_Flows_In_You.mid',
  'Schindlers_List_Theme.mid',
  'Sonate_No__Moonlight_st_Movement.mid',
  'Undertale__Megalovania_Piano_ver_.mid',
  'Utada_Hikaru__First_Love.mid',
  'Liebestraum_S__No__in_A_Major__Liszt.mid',
  'Mariage_dAmour.mid',
  'Je_Te_Laisserai_Des_Mots__Patrick_Watson.mid',
  'TodayNew1.mid',
  'Time_Travel_Theme_by_Jay_Chou_from_Secret__film (1).mid',
];

// Default demo song (88 lanes, sparse)
const DEFAULT_SONG = [
  [24, 1000], [36, 1500], [48, 2000], [60, 2500], [72, 3000],
  [30, 3500], [42, 4500], [54, 5500], [66, 6500], [40, 7500], [52, 8500],
];

const HIT_ZONE_Y = 600; // hit line position (notes-container coords; overlay is inside notes-container)
const SPAWN_Y = 0;
const HIT_WINDOW = 80;
const LEAD_IN_MS = 3000; // extra time before first note hits / MIDI starts (notes get 3s to drop)

// Two-player: Player 1 keys (q w e r t g f d s a z x c v b + Space). Player 2: y u i o p h j k l ; n m , . / + arrow keys
const P1_KEYS_TWO_PLAYER = new Set('qwertgfdsazxcvb'.split(''));
const P2_KEYS_TWO_PLAYER = new Set('yuiophjkl;nm,./'.split(''));
const P2_ARROW_KEYS = new Set(['arrowleft', 'arrowright', 'arrowup', 'arrowdown']);

const STORAGE_KEY = 'pianoHero_options';
function loadStoredOptions() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const o = JSON.parse(raw);
    return {
      song: typeof o.song === 'string' ? o.song : '',
      speed: typeof o.speed === 'number' && o.speed >= 50 && o.speed <= 400 ? o.speed : 150,
      sound: o.sound === 'piano' || o.sound === 'musicbox' ? o.sound : 'piano',
      mouseViolin: o.mouseViolin === true,
      gameMode: o.gameMode === 'two' ? 'two' : 'single',
    };
  } catch {
    return {};
  }
}

// MIDI number (21–108) to note name for Tone.Sampler (e.g. 69 -> "A4")
function midiToNoteName(midi) {
  const names = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const octave = Math.floor(midi / 12) - 1;
  return names[midi % 12] + octave;
}

function App() {
  const [gameState, setGameState] = useState('start');
  const [gameMode, setGameMode] = useState(() => (loadStoredOptions().gameMode === 'two' ? 'two' : 'single'));
  const [score, setScore] = useState(0);
  const [maxScore, setMaxScore] = useState(0);
  const [currentMaxScore, setCurrentMaxScore] = useState(0);
  const [score2, setScore2] = useState(0);
  const [maxScore2, setMaxScore2] = useState(0);
  const [currentMaxScore2, setCurrentMaxScore2] = useState(0);
  const [notes, setNotes] = useState([]);
  const [laneEffects, setLaneEffects] = useState(Array(NUM_LANES).fill(false));
  const [laneEffects2, setLaneEffects2] = useState(Array(NUM_LANES).fill(false));
  const [showHitEffect, setShowHitEffect] = useState({});
  const [selectedSpeed, setSelectedSpeed] = useState(() => (loadStoredOptions().speed ?? 150));
  const [hitFeedback, setHitFeedback] = useState(null);
  const [hitFeedback2, setHitFeedback2] = useState(null);
  const [selectedSong, setSelectedSong] = useState(() => (loadStoredOptions().song ?? ''));
  const [songData, setSongData] = useState(DEFAULT_SONG);
  const [songName, setSongName] = useState('Demo Song');
  const [isLoading, setIsLoading] = useState(false);
  const [soundMode, setSoundMode] = useState(() => (loadStoredOptions().sound ?? 'piano')); // 'waveform' | 'piano' | 'musicbox'
  const [mouseViolinMode, setMouseViolinMode] = useState(() => (loadStoredOptions().mouseViolin ?? false));
  const [pianoLoading, setPianoLoading] = useState(false);
  const [pianoReady, setPianoReady] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const songDataRef = useRef(DEFAULT_SONG);
  
  const audioContextRef = useRef(null);
  const pianoRef = useRef(null);
  const musicBoxRef = useRef(null);
  const gameStartTimeRef = useRef(0);
  const pausedAtTimeRef = useRef(0);
  const animationFrameRef = useRef(null);
  const activeNotesRef = useRef([]);
  const noteIndexRef = useRef(0);
  const playbackTimeoutsRef = useRef([]);
  const noteSpeedRef = useRef(150);
  const currentMaxScoreRef = useRef(0);
  const currentMaxScore2Ref = useRef(0);
  const pianoRestoreFetchRef = useRef(null);
  const scrollCooldownRef = useRef(false);
  const scrollCooldownIdRef = useRef(null);

  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  }, []);

  // Persist main screen selections so they're restored when coming back or after refresh
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        song: selectedSong,
        speed: selectedSpeed,
        sound: soundMode,
        mouseViolin: mouseViolinMode,
        gameMode,
      }));
    } catch (_) {}
  }, [selectedSong, selectedSpeed, soundMode, mouseViolinMode, gameMode]);

  // Load Tone.js Piano when user selects piano (Salamander Grand Piano, MIT)
  // If public/piano/ has samples (after npm run download-piano), load from there for instant play.
  // Library requests filenames like "A0v8.[mp3|ogg]" — we only have .mp3 locally, so we intercept fetch.
  const PIANO_CDN = 'https://tambien.github.io/Piano/audio/';
  const PIANO_LOCAL = `${window.location.origin}${process.env.PUBLIC_URL || ''}/piano/`;
  const [pianoLoadSource, setPianoLoadSource] = useState(null); // 'local' | 'cdn' | null

  useEffect(() => {
    if (soundMode !== 'piano') return;
    if (pianoRef.current) return;
    setPianoLoading(true);
    setPianoLoadSource(null);
    // Check if local samples exist (one known file from download-piano script)
    const localCheck = fetch(`${process.env.PUBLIC_URL || ''}/piano/pedalD1.mp3`, { method: 'HEAD' })
      .then(r => r.ok)
      .catch(() => false);

    pianoRestoreFetchRef.current = null;
    localCheck.then((useLocal) => {
      const baseUrl = useLocal ? PIANO_LOCAL : PIANO_CDN;
      setPianoLoadSource(useLocal ? 'local' : 'cdn');
      // When loading from local, Piano lib requests ".[mp3|ogg]" — we only have .mp3; intercept fetch
      let restoreFetch = null;
      if (useLocal && typeof window !== 'undefined' && window.fetch) {
        const origFetch = window.fetch;
        restoreFetch = () => { window.fetch = origFetch; };
        pianoRestoreFetchRef.current = restoreFetch;
        window.fetch = function (input, ...args) {
          if (typeof input === 'string' && input.includes('/piano/') && input.endsWith('.[mp3|ogg]')) {
            input = input.replace('.[mp3|ogg]', '.mp3');
          }
          return origFetch.call(this, input, ...args);
        };
      }
      const piano = new Piano({ maxPolyphony: 32, url: baseUrl }).toDestination();
      pianoRef.current = piano;
      const done = () => {
        if (pianoRestoreFetchRef.current) { pianoRestoreFetchRef.current(); pianoRestoreFetchRef.current = null; }
        setPianoReady(true);
        setPianoLoading(false);
      };
      const fail = (err) => {
        if (pianoRestoreFetchRef.current) { pianoRestoreFetchRef.current(); pianoRestoreFetchRef.current = null; }
        console.warn('Piano load failed:', err);
        if (useLocal) {
          piano.dispose();
          pianoRef.current = null;
          setPianoLoadSource('cdn');
          const fallback = new Piano({ maxPolyphony: 32, url: PIANO_CDN }).toDestination();
          pianoRef.current = fallback;
          fallback.load().then(done).catch((e) => {
            console.error('Piano load failed:', e);
            setPianoLoading(false);
          });
        } else {
          setPianoLoading(false);
        }
      };
      piano.load().then(done).catch(fail);
    });

    return () => {
      if (pianoRestoreFetchRef.current) { pianoRestoreFetchRef.current(); pianoRestoreFetchRef.current = null; }
      if (pianoRef.current) {
        pianoRef.current.dispose();
        pianoRef.current = null;
      }
      setPianoReady(false);
      setPianoLoadSource(null);
    };
  }, [soundMode]);

  // Third sound: Music box (synthesized – bright, short pluck, no samples to load)
  useEffect(() => {
    if (soundMode !== 'musicbox') return;
    if (musicBoxRef.current) return;
    const musicBox = new Tone.PolySynth(Tone.Synth, {
      maxPolyphony: 32,
      options: {
        oscillator: { type: 'triangle' },
        envelope: { attack: 0.001, decay: 0.12, sustain: 0, release: 0.3 },
      },
    }).toDestination();
    musicBoxRef.current = musicBox;
    return () => {
      if (musicBoxRef.current) {
        musicBoxRef.current.dispose();
        musicBoxRef.current = null;
      }
    };
  }, [soundMode]);

  const playNote = useCallback((lane, duration = 0.5, velocity = 0.8) => {
    const midi = MIDI_LOW + lane;
    const vel = typeof velocity === 'number' ? Math.max(0.01, Math.min(1, velocity)) : 0.8;
    if (soundMode === 'piano' && pianoRef.current?.loaded) {
      Tone.start();
      const p = pianoRef.current;
      p.keyDown({ midi, velocity: vel });
      setTimeout(() => {
        if (p.loaded) p.keyUp({ midi });
      }, duration * 1000);
      return;
    }
    if (soundMode === 'musicbox' && musicBoxRef.current) {
      Tone.start();
      try {
        musicBoxRef.current.triggerAttackRelease(midiToNoteName(midi), duration, undefined, vel);
      } catch (_) {}
      return;
    }
    const ctx = initAudio();
    if (ctx.state === 'suspended') ctx.resume();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    oscillator.frequency.value = NOTE_FREQUENCIES[lane];
    oscillator.type = 'sine';
    gainNode.gain.setValueAtTime(0.4 * vel, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration);
  }, [initAudio, soundMode]);

  const loadMidiSong = useCallback(async (filename) => {
    const response = await fetch(`/midi/${encodeURIComponent(filename)}`);
    if (!response.ok) {
      throw new Error(`Failed to load MIDI: ${response.status} ${response.statusText}`);
    }
    const arrayBuffer = await response.arrayBuffer();
    const midi = new Midi(arrayBuffer);
    
    const notes = [];
    const tracks = midi.tracks;
    
    // Map MIDI note to lane 0–87 (88-key piano: A0 = 21 → lane 0, C8 = 108 → lane 87)
    const midiToLane = (midi) => Math.max(0, Math.min(NUM_LANES - 1, midi - MIDI_LOW));
    
    for (const track of tracks) {
      if (track.notes && track.notes.length > 0) {
        for (const note of track.notes) {
          const lane = midiToLane(note.midi);
          const timeMs = note.time * 1000;
          const velocity = typeof note.velocity === 'number' ? note.velocity : 0.8;
          const existingNote = notes.find(n =>
            n[0] === lane && Math.abs(n[1] - timeMs) < 60
          );
          if (!existingNote) {
            notes.push([lane, timeMs, velocity]);
          }
        }
      }
    }
    
    notes.sort((a, b) => a[1] - b[1]);
    return notes;
  }, []);

  const startGame = useCallback(async (speed = selectedSpeed, song = selectedSong) => {
    if (!song) {
      alert('Please select a song first!');
      return;
    }
    
    // Show loading while loading MIDI
    setIsLoading(true);
    
    let midiNotes;
    try {
      midiNotes = await loadMidiSong(song);
    } catch (error) {
      console.error('Error loading MIDI:', error);
      alert(`Could not load "${song}". Make sure the file exists in public/midi/.\n\n${error.message}`);
      setIsLoading(false);
      return;
    }
    
    songDataRef.current = midiNotes;
    setSongName(song.replace('.mid', '').replace(/_/g, ' '));
    
    const ctx = initAudio();
    if (ctx.state === 'suspended') ctx.resume();
    Tone.start();
    const isTwoPlayer = gameMode === 'two';
    const totalMax = BASE_POINTS * midiNotes.length;
    setScore(0);
    setMaxScore(totalMax);
    setCurrentMaxScore(0);
    currentMaxScoreRef.current = 0;
    setScore2(0);
    setMaxScore2(isTwoPlayer ? totalMax : 0);
    setCurrentMaxScore2(0);
    currentMaxScore2Ref.current = 0;
    setNotes([]);
    activeNotesRef.current = [];
    noteIndexRef.current = 0;
    noteSpeedRef.current = speed;
    playbackTimeoutsRef.current.forEach(id => clearTimeout(id));
    playbackTimeoutsRef.current = [];

    gameStartTimeRef.current = Date.now();
    const SPEED_1X = 150;
    midiNotes.forEach((noteData) => {
      const [lane, timeMs, velocity] = noteData.length >= 3 ? noteData : [noteData[0], noteData[1], 0.8];
      const realTimeMs = LEAD_IN_MS + (timeMs * SPEED_1X) / speed;
      const id = setTimeout(() => {
        playNote(lane, 0.5, velocity);
      }, realTimeMs);
      playbackTimeoutsRef.current.push(id);
    });

    setIsLoading(false);
    setGameState('playing');
  }, [initAudio, selectedSpeed, selectedSong, loadMidiSong, playNote, gameMode]);

  const goToHome = useCallback(() => {
    playbackTimeoutsRef.current.forEach(id => clearTimeout(id));
    playbackTimeoutsRef.current = [];
    setGameState('start');
  }, []);

  const pauseGame = useCallback(() => {
    playbackTimeoutsRef.current.forEach(id => clearTimeout(id));
    playbackTimeoutsRef.current = [];
    pausedAtTimeRef.current = Date.now() - gameStartTimeRef.current;
    setGameState('paused');
  }, []);

  const resumeGame = useCallback(() => {
    const ctx = initAudio();
    if (ctx.state === 'suspended') ctx.resume();
    const pausedAt = pausedAtTimeRef.current;
    gameStartTimeRef.current = Date.now() - pausedAt;
    const speed = noteSpeedRef.current;
    const SPEED_1X = 150;
    playbackTimeoutsRef.current = [];
    songDataRef.current.forEach((noteData) => {
      const [lane, timeMs, velocity] = noteData.length >= 3 ? noteData : [noteData[0], noteData[1], 0.8];
      const realTimeMs = LEAD_IN_MS + (timeMs * SPEED_1X) / speed;
      if (realTimeMs > pausedAt) {
        const delayMs = realTimeMs - pausedAt;
        const id = setTimeout(() => {
          playNote(lane, 0.5, velocity);
        }, delayMs);
        playbackTimeoutsRef.current.push(id);
      }
    });
    setGameState('playing');
  }, [playNote, initAudio]);

  // playerId: 1 = left/single, 2 = right (two-player only). Two-player: both see same notes; first to hit gets points.
  const handleHit = useCallback((playerId) => {
    if (gameState !== 'playing') return;
    const isTwo = gameMode === 'two';
    const currentTime = Date.now() - gameStartTimeRef.current;
    const notesInWindow = activeNotesRef.current.filter(note =>
      Math.abs(note.hitTime - currentTime) <= HIT_WINDOW
    );
    const count = notesInWindow.length;

    if (isTwo) {
      if (playerId === 1) {
        setLaneEffects(prev => prev.map(() => true));
        setTimeout(() => setLaneEffects(prev => prev.map(() => false)), 100);
      } else {
        setLaneEffects2(prev => prev.map(() => true));
        setTimeout(() => setLaneEffects2(prev => prev.map(() => false)), 100);
      }
    } else {
      setLaneEffects(prev => prev.map(() => true));
      setTimeout(() => setLaneEffects(prev => prev.map(() => false)), 100);
    }

    if (count >= 1) {
      const points = BASE_POINTS * count;
      const hitIds = new Set(notesInWindow.map(n => n.id));
      setShowHitEffect(prev => {
        const next = { ...prev };
        notesInWindow.forEach(n => { next[n.id] = true; });
        return next;
      });
      activeNotesRef.current = activeNotesRef.current.filter(n => !hitIds.has(n.id));
      const fb = { text: `+${points}`, color: '#4ecdc4', lane: NUM_LANES / 2 };
      if (playerId === 1) {
        setHitFeedback(fb);
        setTimeout(() => setHitFeedback(null), 500);
        setScore(prev => prev + points);
      } else {
        setHitFeedback2(fb);
        setTimeout(() => setHitFeedback2(null), 500);
        setScore2(prev => prev + points);
      }
    } else {
      const missFb = { text: 'Miss', color: '#ff6b6b', lane: NUM_LANES / 2 };
      if (playerId === 1) {
        setHitFeedback(missFb);
        setTimeout(() => setHitFeedback(null), 500);
      } else {
        setHitFeedback2(missFb);
        setTimeout(() => setHitFeedback2(null), 500);
      }
    }
  }, [gameState, gameMode]);

  useEffect(() => {
    const isTwo = gameMode === 'two';
    const handleKeyDown = (e) => {
      if (e.repeat) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      e.preventDefault();
      if (isTwo && gameState === 'playing') {
        // P1: q w e r t g f d s a z x c v b, Space. P2: y u i o p h j k l ; n m , . / + Left/Right/Up/Down arrows
        const key = (e.key || '').toLowerCase();
        if (P2_KEYS_TWO_PLAYER.has(key) || P2_ARROW_KEYS.has(key)) handleHit(2);
        else if (key === ' ' || P1_KEYS_TWO_PLAYER.has(key)) handleHit(1);
      } else {
        handleHit(1);
      }
    };
    const SCROLL_START_MS = 400;
    const handleWheel = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (isTwo && gameState === 'playing') return;
      if (scrollCooldownRef.current) return;
      scrollCooldownRef.current = true;
      if (scrollCooldownIdRef.current) clearTimeout(scrollCooldownIdRef.current);
      scrollCooldownIdRef.current = setTimeout(() => {
        scrollCooldownRef.current = false;
        scrollCooldownIdRef.current = null;
      }, SCROLL_START_MS);
      handleHit(1);
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('wheel', handleWheel);
    return () => {
      if (scrollCooldownIdRef.current) clearTimeout(scrollCooldownIdRef.current);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('wheel', handleWheel);
    };
  }, [handleHit, gameState, gameMode]);

  // Lock body scroll during play so wheel still fires all events but page doesn't scroll
  useEffect(() => {
    if (gameState === 'playing' || gameState === 'paused') document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [gameState]);

  // Disable right-click context menu during play so right mouse key can be used to hit notes
  useEffect(() => {
    const handleContextMenu = (e) => {
      if (gameState === 'playing') e.preventDefault();
    };
    window.addEventListener('contextmenu', handleContextMenu);
    return () => window.removeEventListener('contextmenu', handleContextMenu);
  }, [gameState]);

  useEffect(() => {
    if (gameState !== 'playing') return;

    const gameLoop = () => {
      const currentTime = Date.now() - gameStartTimeRef.current;
      const speed = noteSpeedRef.current;
      
      // Time for note to fall from spawn (top) to hit zone (same speed for spawn + position)
      const fallTime = (HIT_ZONE_Y / speed) * 1000;
      const SPEED_1X = 150;
      const midiTimeToReal = (t) => (t * SPEED_1X) / speed;
      // Lead-in: notes reach hit zone 5s later so they have extra space to drop
      const realHitTime = (time) => LEAD_IN_MS + midiTimeToReal(time);

      // Spawn new notes
      while (noteIndexRef.current < songDataRef.current.length) {
        const data = songDataRef.current[noteIndexRef.current];
        const [lane, time, velocity] = data.length >= 3 ? data : [data[0], data[1], 0.8];
        const hitTime = realHitTime(time);
        if (hitTime - fallTime <= currentTime) {
          const note = {
            id: `${lane}-${time}-${Date.now()}`,
            lane,
            hitTime,
            velocity,
            spawnTime: currentTime,
          };
          activeNotesRef.current.push(note);
          noteIndexRef.current++;
        } else {
          break;
        }
      }

      // Update note positions (note reaches HIT_ZONE_Y exactly at hitTime)
      setNotes(prev => {
        return activeNotesRef.current.map(note => {
          const elapsed = currentTime - (note.hitTime - fallTime);
          const y = SPAWN_Y + (elapsed / 1000) * speed;
          return { ...note, y };
        }).filter(note => note.y < HIT_ZONE_Y + 100);
      });
      
      // Current max: max score achievable from notes that have already passed the hit window
      const isTwo = gameMode === 'two';
      const pastCount = songDataRef.current.filter((data) => {
        const time = data.length >= 3 ? data[1] : data[1];
        const hitTime = LEAD_IN_MS + (time * SPEED_1X) / speed;
        return currentTime > hitTime + HIT_WINDOW;
      }).length;
      const newCurrentMax = BASE_POINTS * pastCount;
      if (newCurrentMax !== currentMaxScoreRef.current) {
        currentMaxScoreRef.current = newCurrentMax;
        setCurrentMaxScore(newCurrentMax);
      }
      if (isTwo && newCurrentMax !== currentMaxScore2Ref.current) {
        currentMaxScore2Ref.current = newCurrentMax;
        setCurrentMaxScore2(newCurrentMax);
      }

      // Check for missed notes
      const missedNotes = activeNotesRef.current.filter(note => 
        currentTime > note.hitTime + HIT_WINDOW
      );
      
      if (missedNotes.length > 0) {
        missedNotes.forEach(note => {
          const idx = activeNotesRef.current.indexOf(note);
          if (idx !== -1) activeNotesRef.current.splice(idx, 1);
        });
      }
      
      // Check game over
      if (noteIndexRef.current >= songDataRef.current.length && activeNotesRef.current.length === 0) {
        playbackTimeoutsRef.current.forEach(id => clearTimeout(id));
        playbackTimeoutsRef.current = [];
        setGameState('gameover');
        return;
      }
      
      animationFrameRef.current = requestAnimationFrame(gameLoop);
    };
    
    animationFrameRef.current = requestAnimationFrame(gameLoop);
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [gameState, gameMode]);

  return (
    <div className="App">
      <div className="game-container">
        <h1 className="game-title">🎹 Piano Hero</h1>
        {(gameState === 'playing' || gameState === 'gameover' || gameState === 'paused') && (
          <div className="game-song-title">{songName}</div>
        )}
        {gameState === 'start' && (
          <div className="menu">
            {showAbout ? (
              <div className="about-panel">
                <h2 style={{ marginBottom: 16, color: '#ffd700' }}>Credits / Third-Party Licenses</h2>
                <div className="about-content">
                  <p style={{ marginBottom: 12, fontWeight: 'bold' }}>Tone.js Piano</p>
                  <p style={{ marginBottom: 8, fontSize: '0.9rem' }}>Copyright (c) 2016-2018 Yotam Mann</p>
                  <p style={{ marginBottom: 16, fontSize: '0.9rem' }}>Licensed under the MIT License.</p>
                  <p style={{ marginBottom: 8, fontSize: '0.85rem', color: '#aaa' }}><a href="https://github.com/Tonejs/Piano" target="_blank" rel="noopener noreferrer" style={{ color: '#4ecdc4' }}>https://github.com/Tonejs/Piano</a></p>
                  <p style={{ marginTop: 20, marginBottom: 12, fontWeight: 'bold' }}>Piano samples: Salamander Grand Piano</p>
                  <p style={{ marginBottom: 8, fontSize: '0.9rem' }}>By Alexander Holm. Licensed under Creative Commons Attribution 3.0 (CC BY 3.0).</p>
                  <p style={{ marginBottom: 16, fontSize: '0.85rem', color: '#aaa' }}><a href="https://freepats.zenvoid.org/" target="_blank" rel="noopener noreferrer" style={{ color: '#4ecdc4' }}>https://freepats.zenvoid.org/</a></p>
                </div>
                <button type="button" className="start-btn" style={{ marginTop: 20 }} onClick={() => setShowAbout(false)}>Back</button>
              </div>
            ) : isLoading ? (
              <p className="loading">Loading song...</p>
            ) : (
              <>
                <div className="mode-select" style={{ marginBottom: 16 }}>
                  <p>Mode:</p>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input type="radio" name="gameMode" checked={gameMode === 'single'} onChange={() => setGameMode('single')} />
                      <span>Single Player</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input type="radio" name="gameMode" checked={gameMode === 'two'} onChange={() => setGameMode('two')} />
                      <span>Two Players (same computer)</span>
                    </label>
                  </div>
                </div>
                <div className="sound-select">
                  <p>Sound (compare quality):</p>
                  <select
                    value={soundMode}
                    onChange={(e) => setSoundMode(e.target.value)}
                    className="song-dropdown"
                  >
                    <option value="piano">Piano</option>
                    <option value="waveform">Wave Form</option>
                    <option value="musicbox">Music Box</option>
                  </select>
                  {soundMode === 'piano' && pianoLoading && (
                    <p className="loading-piano">Loading piano samples… (usually 15–60 sec, first time only)</p>
                  )}
                  {soundMode === 'musicbox' && (
                    <p style={{ fontSize: '0.85rem', color: '#4ecdc4' }}>Music box ready. Start game to compare.</p>
                  )}
                </div>
                <div className="song-select">
                  <p>Select Song:</p>
                  <select 
                    value={selectedSong} 
                    onChange={(e) => setSelectedSong(e.target.value)}
                    className="song-dropdown"
                  >
                    <option value="" disabled>Select a song...</option>
                    {MIDI_FILES.map((file) => (
                      <option key={file} value={file}>
                        🎹 {file.replace('.mid', '').replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="speed-select">
                  <p>Select Speed: {selectedSpeed}</p>
                  <div className="speed-slider">
                    <input
                      type="range"
                      min="50"
                      max="400"
                      step="25"
                      value={selectedSpeed}
                      onChange={(e) => setSelectedSpeed(Number(e.target.value))}
                    />
                    <div className="speed-labels">
                      <span>Slow</span>
                      <span>Normal</span>
                      <span>Fast</span>
                    </div>
                  </div>
                </div>
                <button 
                  className="start-btn" 
                  onClick={() => startGame(selectedSpeed, selectedSong)}
                  disabled={soundMode === 'piano' && !pianoReady}
                >
                  {soundMode === 'piano' && !pianoReady ? 'Loading piano…' : 'Start Game'}
                </button>
                <button type="button" className="about-btn" onClick={() => setShowAbout(true)}>Credits</button>
              </>
            )}
          </div>
        )}
        
        {(gameState === 'playing' || gameState === 'gameover' || gameState === 'paused') && (
          <>
            <button type="button" className="game-back-btn" onClick={goToHome} title="Back to Home" aria-label="Back to Home">
              ←
            </button>
            {gameState === 'paused' && (
              <div className="gameover-main-menu game-paused-overlay">
                <h2 style={{ fontSize: '2rem', marginBottom: 16, color: '#fff' }}>Paused</h2>
                <button type="button" className="start-btn" onClick={resumeGame}>Resume</button>
              </div>
            )}
            {gameMode === 'two' ? (
              <div className="game-two-columns">
                <div className="game-player-side">
                  <div className="hud hud-player">
                    <div className="score">Score: {score} / {currentMaxScore} - {currentMaxScore > 0 ? Math.round((score / currentMaxScore) * 100) : 0}% (Max: {maxScore})</div>
                    <div className="hud-hint">P1: Q W E R T G F D S A Z X C V B, Space</div>
                  </div>
                  <div className="lanes lanes-88">
                    {Array.from({ length: NUM_LANES }, (_, i) => i).map(laneIndex => (
                      <div key={laneIndex} className={`lane ${laneEffects[laneIndex] ? 'active' : ''}`} style={{ backgroundColor: laneEffects[laneIndex] ? 'rgba(78, 205, 196, 0.25)' : undefined, borderColor: laneEffects[laneIndex] ? '#4ecdc4' : 'rgba(255,255,255,0.06)' }} />
                    ))}
                  </div>
                  <div className="notes-container">
                    <div className="hit-zone-overlay" style={{ top: HIT_ZONE_Y }}><span className="hit-zone-label hit-zone-left">Hit zone</span></div>
                    {notes.map(note => (
                      <div key={`p1-${note.id}`} className={`note ${showHitEffect[note.id] ? 'hit' : ''}`} style={{ left: `${(note.lane / NUM_LANES) * 100}%`, width: `${100 / NUM_LANES}%`, top: note.y, backgroundColor: LANE_COLORS[note.lane] }} />
                    ))}
                    {(gameState === 'playing' || gameState === 'paused') && hitFeedback && <div className="hit-feedback" style={{ left: `${(hitFeedback.lane / NUM_LANES) * 100 + (100 / NUM_LANES / 2)}%`, top: HIT_ZONE_Y - 30, color: hitFeedback.color }}>{hitFeedback.text}</div>}
                  </div>
                </div>
                <div className="game-player-side game-player-side--right">
                  <div className="hud hud-player">
                    <div className="score">Score: {score2} / {currentMaxScore2} - {currentMaxScore2 > 0 ? Math.round((score2 / currentMaxScore2) * 100) : 0}% (Max: {maxScore2})</div>
                    <div className="hud-hint">P2: Y U I O P H J K L ; N M , . / + ← → ↑ ↓</div>
                  </div>
                  <div className="lanes lanes-88">
                    {Array.from({ length: NUM_LANES }, (_, i) => i).map(laneIndex => (
                      <div key={laneIndex} className={`lane ${laneEffects2[laneIndex] ? 'active' : ''}`} style={{ backgroundColor: laneEffects2[laneIndex] ? 'rgba(255, 180, 100, 0.25)' : undefined, borderColor: laneEffects2[laneIndex] ? '#ffb464' : 'rgba(255,255,255,0.06)' }} />
                    ))}
                  </div>
                  <div className="notes-container">
                    <div className="hit-zone-overlay" style={{ top: HIT_ZONE_Y }}><span className="hit-zone-label hit-zone-left">Hit zone</span></div>
                    {notes.map(note => (
                      <div key={`p2-${note.id}`} className={`note ${showHitEffect[note.id] ? 'hit' : ''}`} style={{ left: `${(note.lane / NUM_LANES) * 100}%`, width: `${100 / NUM_LANES}%`, top: note.y, backgroundColor: LANE_COLORS[note.lane] }} />
                    ))}
                    {(gameState === 'playing' || gameState === 'paused') && hitFeedback2 && <div className="hit-feedback" style={{ left: `${(hitFeedback2.lane / NUM_LANES) * 100 + (100 / NUM_LANES / 2)}%`, top: HIT_ZONE_Y - 30, color: hitFeedback2.color }}>{hitFeedback2.text}</div>}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="hud">
                  <div className="sound-badge">{soundMode === 'piano' ? '🔊 Piano' : soundMode === 'musicbox' ? '🔊 Music box' : '🔊 Waveform'}</div>
                  <div className="score">Score: {score} / {currentMaxScore} - {currentMaxScore > 0 ? Math.round((score / currentMaxScore) * 100) : 0}% (Max: {maxScore})</div>
                </div>
                <div className="lanes lanes-88">
                  {Array.from({ length: NUM_LANES }, (_, i) => i).map(laneIndex => (
                    <div key={laneIndex} className={`lane ${laneEffects[laneIndex] ? 'active' : ''}`} style={{ backgroundColor: laneEffects[laneIndex] ? 'rgba(78, 205, 196, 0.25)' : undefined, borderColor: laneEffects[laneIndex] ? '#4ecdc4' : 'rgba(255,255,255,0.06)' }} />
                  ))}
                </div>
                <div className="notes-container">
                  <div className="hit-zone-overlay" style={{ top: HIT_ZONE_Y }}><span className="hit-zone-label hit-zone-left">Any key / Scroll to hit</span></div>
                  {notes.map(note => (
                    <div key={note.id} className={`note ${showHitEffect[note.id] ? 'hit' : ''}`} style={{ left: `${(note.lane / NUM_LANES) * 100}%`, width: `${100 / NUM_LANES}%`, top: note.y, backgroundColor: LANE_COLORS[note.lane] }} />
                  ))}
                  {(gameState === 'playing' || gameState === 'paused') && hitFeedback && <div className="hit-feedback" style={{ left: `${(hitFeedback.lane / NUM_LANES) * 100 + (100 / NUM_LANES / 2)}%`, top: HIT_ZONE_Y - 30, color: hitFeedback.color }}>{hitFeedback.text}</div>}
                </div>
              </>
            )}
            {gameState === 'gameover' && (
              <div className="gameover-main-menu">
                <button className="start-btn" onClick={goToHome}>Main Menu</button>
              </div>
            )}
            {gameState === 'playing' && (
              <div className="game-stop-bar">
                <button type="button" className="game-stop-btn" onClick={pauseGame}>Pause Game</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default App;
